import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, AlertCircle, Mail, MessageSquare } from "lucide-react";

export default function RateLimitModal() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleRateLimit = () => {
      setIsOpen(true);
    };

    window.addEventListener("rate-limit-exceeded", handleRateLimit);
    return () => {
      window.removeEventListener("rate-limit-exceeded", handleRateLimit);
    };
  }, []);

  const onClose = () => setIsOpen(false);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 select-text"
          >
            {/* Top decorative gradient */}
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-400 via-rose-500 to-purple-600" />

            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all z-20"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            <div className="p-8 md:p-10 flex flex-col items-center text-center">
              {/* Warning Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: "spring",
                  damping: 12,
                  stiffness: 200,
                  delay: 0.2,
                }}
                className="w-20 h-20 bg-orange-100 dark:bg-orange-900/30 rounded-3xl flex items-center justify-center mb-6 text-orange-600 dark:text-orange-400 shadow-inner relative"
              >
                <AlertCircle size={40} strokeWidth={2.5} />
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute inset-0 bg-orange-400/20 rounded-3xl"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-4 tracking-tight leading-tight">
                  Упс! С вашего аккаунта поступает слишком много запросов
                </h2>

                <div className="space-y-4 text-slate-600 dark:text-slate-400 text-sm md:text-base leading-relaxed font-medium">
                  <p>Зайдите к нам через 20 минут.</p>

                  <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800 text-xs md:text-sm">
                    <p className="mb-2">
                      Если вы видите эту ошибку ошибочно и часто, напишите нам:
                    </p>
                    <a
                      href="mailto:support@invoitio.com"
                      className="inline-flex items-center gap-2 text-indigo-600 dark:text-indigo-400 font-bold hover:underline"
                    >
                      <Mail size={14} />
                      support@invoitio.com
                    </a>
                  </div>

                  <p className="text-slate-400 dark:text-slate-500 font-bold mt-4">
                    Спасибо заранее за понимание 🙏🏻
                  </p>
                </div>
              </motion.div>

              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                onClick={onClose}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="mt-8 w-full py-4 px-6 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 dark:from-slate-700 dark:to-slate-800 text-white font-black text-sm shadow-xl transition-all duration-300"
              >
                Понятно
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
